import { useForm, useFieldArray } from "react-hook-form";
import {
  Modal,
  Stack,
  Group,
  Button,
  Divider,
  Text,
  ActionIcon,
  Card,
} from "@mantine/core";
import { TextInput, Select, Checkbox } from "react-hook-form-mantine";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useEffect } from "react";
import resolver from "@congrs/resolver";
import { web_updateRegistrationCustomFieldSchema } from "@congrs/schemas";
import { useUpdateRegistrationCustomField } from "../../api/registrationCustomFields";
import { extractDirtyData } from "../../utils/dirtyData";
import {
  mergeI18nWithDefaults,
  makeI18nDefaultValue,
} from "../../utils/i18nUtils";
import LocalizedInput from "../common/LocalizedInput";

function EditCustomFieldModal({ opened, onClose, fieldData, event }) {
  const updateField = useUpdateRegistrationCustomField(fieldData.id);

  const mainLanguage = event?.language || "en";
  const otherLanguages = event?.secondaryLanguages || [];

  const defaultValues = {
    labelI18n: mergeI18nWithDefaults({}, mainLanguage, otherLanguages),
    key: "",
    type: "text",
    required: false,
    options: [],
    showOnRegistrationForm: true,
  };

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty, errors, dirtyFields },
  } = useForm({
    defaultValues,
    resolver: resolver(web_updateRegistrationCustomFieldSchema),
  });

  const selectedType = watch("type");
  const showOnRegistrationForm = watch("showOnRegistrationForm");
  const { fields, append, remove } = useFieldArray({
    control,
    name: "options",
  });

  useEffect(() => {
    if (fieldData) {
      const options = fieldData.optionsI18n
        ? fieldData.optionsI18n.map((nameI18n) => ({
            nameI18n: mergeI18nWithDefaults(
              nameI18n,
              mainLanguage,
              otherLanguages
            ),
          }))
        : [];

      reset({
        labelI18n: mergeI18nWithDefaults(
          fieldData.labelI18n,
          mainLanguage,
          otherLanguages
        ),
        key: fieldData.key,
        type: fieldData.type,
        required: fieldData.required,
        options,
        showOnRegistrationForm: fieldData.showOnRegistrationForm,
      });
    }
  }, [fieldData, mainLanguage, otherLanguages, reset]);

  const onSubmit = (values) => {
    const payload = extractDirtyData(values, dirtyFields);

    if (selectedType === "select" || selectedType === "multiselect") {
      payload.options = Array.isArray(values.options) ? values.options : [];
    }

    updateField.mutate(payload, {
      onSuccess: () => {
        reset(values);
        onClose();
      },
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const localizedInputFields = [
    {
      nameBase: "labelI18n",
      label: "Label",
      component: TextInput,
      props: {
        size: "sm",
        placeholder: "Label",
        withAsterisk: true,
      },
    },
  ];

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={<Text fw={600}>Edit Custom Field</Text>}
      size="lg"
      centered
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="md">
          <LocalizedInput
            control={control}
            fields={localizedInputFields}
            mainLanguage={mainLanguage}
            otherLanguages={otherLanguages}
          />

          <TextInput
            control={control}
            name="key"
            label="Key"
            error={errors.key?.message}
          />

          <Select
            control={control}
            name="type"
            label="Type"
            data={[
              { value: "text", label: "Text" },
              { value: "boolean", label: "Boolean" },
              { value: "select", label: "Select" },
              { value: "multiselect", label: "Multiselect" },
              { value: "date", label: "Date" },
              { value: "country", label: "Country" },
            ]}
            allowDeselect={false}
            error={errors.type?.message}
          />

          <Checkbox
            control={control}
            name="showOnRegistrationForm"
            label="Show on registration form"
          />

          {showOnRegistrationForm && (
            <Checkbox control={control} name="required" label="Required" />
          )}

          <Text size="sm" fw={500}>
            Applied To: {fieldData.appliedTo}
          </Text>

          {(selectedType === "select" || selectedType === "multiselect") && (
            <Stack gap="xs">
              <Divider label="Options" />

              {fields.map((opt, index) => (
                <Card key={opt.id} withBorder p="sm" radius="sm">
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={500}>
                      Option {index + 1}
                    </Text>
                    <ActionIcon
                      color="red"
                      variant="light"
                      onClick={() => remove(index)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                  <LocalizedInput
                    control={control}
                    fields={[
                      {
                        nameBase: `options.${index}.nameI18n`,
                        label: "Name",
                        component: TextInput,
                        props: {
                          size: "sm",
                          placeholder: `Option ${index + 1}`,
                          withAsterisk: true,
                        },
                      },
                    ]}
                    mainLanguage={mainLanguage}
                    otherLanguages={otherLanguages}
                  />
                </Card>
              ))}

              <Button
                leftSection={<IconPlus size={16} />}
                variant="light"
                onClick={() =>
                  append({
                    nameI18n: makeI18nDefaultValue(
                      mainLanguage,
                      otherLanguages
                    ),
                  })
                }
              >
                Add option
              </Button>
            </Stack>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isDirty}
              loading={updateField.isPending}
            >
              Save Changes
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export default EditCustomFieldModal;
